import { FISCAL_SET } from '../../../shared/constants';
import { Order } from '../../../shared/types';

type ReceiptFields = Pick<Order, 'fiscal_req' | 'fiscal_success_ID'>;

/**
 * Whether the success screen offers the "Get fiscal receipt" button.
 *
 * The second clause used a loose `!=`, and that was load-bearing rather than
 * sloppy: `fiscal_success_ID` is a `varchar(255)` holding PRRO receipt ids like
 * `20117311.2.8365` plus the sentinels `-1` and `-2`, so `'-3' != -3` coerced to
 * false while `'-3' !== -3` would be true. The coercion is written out, which is
 * exactly equivalent across every value the column holds.
 *
 * Worth knowing before touching this: `-3` does not occur in the column at all,
 * so the clause currently excludes nothing and reduces to `fiscal_req === 1` —
 * which is 94,783 orders, 94,739 of them carrying the `-2` sentinel. The server
 * only issues a `receipt_key` for 200, so on those the button opens nothing.
 * Whether they should be offered a receipt is a product question, recorded in
 * `.claude/audit/2026-08-24-branching-logic-refactor.md` and left as is by
 * SWRN-2271.
 */
export const hasCashlessReceipt = (order: ReceiptFields): boolean =>
    order.fiscal_req === FISCAL_SET.SUCCESS ||
    (order.fiscal_req === FISCAL_SET.FISCALISATED &&
        Number(order.fiscal_success_ID) !== FISCAL_SET.TIMEOUT);

/**
 * `fiscal_req` values payment-service writes only while a receipt is in flight.
 *
 * `null` is deliberately not here. Loyalty, Technical credit and Masterpass
 * orders keep it forever — 1,626 of them on receipt-issuing machines in the 14
 * days to 2026-10-30; Kaspi too, though its machines issue no receipts — so
 * waiting on it would spin the loader for good. Tranzzo never
 * reaches the success screen with it: CT5 has -1 before status 3, CT1 has -5
 * before the dispense report.
 */
const FISCAL_PENDING: readonly number[] = [FISCAL_SET.DEFERRED, FISCAL_SET.CONFIRMING, FISCAL_SET.ISSUING];

/** Statuses after which no receipt number will follow, but none is owed either. */
const FISCAL_SETTLED: readonly number[] = [FISCAL_SET.SUCCESS, FISCAL_SET.FISCALISATED, FISCAL_SET.NOT_REQUIRED];

export const RECEIPT_POLL_INTERVAL_MS = 1000;
export const RECEIPT_POLL_LIMIT_MS = 30_000;

export type ReceiptState = 'ready' | 'pending' | 'none';

export const isFiscalPending = (fiscalReq: number | null): boolean =>
    fiscalReq !== null && FISCAL_PENDING.includes(fiscalReq);

/**
 * The PRRO timed out (0 with the -3 sentinel) or rejected the receipt (its HTTP
 * code without a number). payment-service does not retry either.
 */
export const isReceiptFailed = ({ fiscal_req, fiscal_success_ID }: ReceiptFields): boolean => {
    if (fiscal_req === null || isFiscalPending(fiscal_req)) return false;
    if (fiscal_req === FISCAL_SET.NOT_REQUIRED) return Number(fiscal_success_ID) === FISCAL_SET.TIMEOUT;
    return !FISCAL_SETTLED.includes(fiscal_req);
};

interface ReceiptContext {
    hasFiscalReceipt: boolean;
    /** The receipt was still in flight when the success screen opened. */
    wasInFlight: boolean;
}

/**
 * What the receipt slot on the success screen shows (SWRN-2271).
 *
 * A receipt that fails while the customer is watching keeps the loader: they
 * were promised one, and the screen does not take that back. An order that
 * arrives already failed shows nothing, exactly as before — on CT1 Portmone the
 * PRRO answer (404, 15, …) is in long before the success screen, about 1,900
 * such orders a day as of 2026-10-30, and a loader there would never resolve.
 */
export const resolveReceiptState = (
    order: ReceiptFields,
    { hasFiscalReceipt, wasInFlight }: ReceiptContext,
): ReceiptState => {
    if (hasCashlessReceipt(order)) return 'ready';
    if (!hasFiscalReceipt || !wasInFlight) return 'none';
    if (isFiscalPending(order.fiscal_req) || isReceiptFailed(order)) return 'pending';
    return 'none';
};

interface ShouldPollOrderInput {
    fiscalReq: number | null;
    hasFiscalReceipt: boolean;
    elapsedMs: number;
}

/**
 * Whether to schedule another poll. The limit is checked against the tick this
 * would schedule, one interval from now — checking `elapsedMs` alone let the
 * last request land up to an interval past 30 s.
 */
export const shouldPollOrder = ({ fiscalReq, hasFiscalReceipt, elapsedMs }: ShouldPollOrderInput): boolean =>
    hasFiscalReceipt &&
    isFiscalPending(fiscalReq) &&
    elapsedMs + RECEIPT_POLL_INTERVAL_MS < RECEIPT_POLL_LIMIT_MS;
